import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/ui/Header';
import Sidebar from 'components/ui/Sidebar';
import Button from 'components/ui/Button';

export default function QrPinRecovery() {
    const navigate = useNavigate();
    const [user] = useState(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } });
    const [permissions, setPermissions] = useState([]);
    const [collapsed, setCollapsed] = useState(true);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [page, setPage] = useState(1);
    const pending = useRef(false);
    const token = localStorage.getItem('token');
    useEffect(() => {
        if (!token) { navigate('/admin'); return; }
        if (user?.role !== 'Admin') { navigate('/dashboard', { replace: true }); return; }
        const controller = new AbortController();
        fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/permission`, {
            headers: { Authorization: `Bearer ${token}` }, signal: controller.signal
        }).then(async res => { if (res.ok) { const data = await res.json(); setPermissions(Array.isArray(data) ? data : []); } })
            .catch(() => {});
        return () => controller.abort();
    }, [token, user?.role, navigate]);
    const chooseFile = event => {
        setPreview(null); setResult(null); setError(''); setPage(1);
        const chosen = event.target.files?.[0];
        if (chosen && (!/\.xlsx$/i.test(chosen.name) || chosen.size > 5 * 1024 * 1024)) {
            setFile(null); setError('Choose an .xlsx workbook up to 5 MB.'); return;
        }
        setFile(chosen || null);
    };
    const submit = async restore => {
        if (user?.role !== 'Admin' || !token || !file || pending.current || (restore && !preview?.token)) return;
        pending.current = true; setBusy(true); setError(''); setResult(null);
        const form = new FormData();
        form.append('file', file);
        if (restore) form.append('token', preview.token);
        else { setPreview(null); setPage(1); }
        try {
            const response = await fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/qr-pin-recovery/${restore ? 'restore' : 'preview'}`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Unable to process this workbook.');
            if (restore) { setResult(data); setPreview(null); }
            else setPreview(data);
        } catch (failure) {
            setError(failure.message || 'Unable to connect. Please try again.');
            setPreview(null);
        } finally { pending.current = false; setBusy(false); }
    };
    const rows = preview?.rows || [];
    const pageCount = Math.ceil(rows.length / 100);
    if (!token || user?.role !== 'Admin') return null;
    return <div className="min-h-screen bg-gray-50">
        <Header user={user} onMenuToggle={() => setCollapsed(!collapsed)} sidebarCollapsed={collapsed} />
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} permissions={permissions} />
        <main className={`pt-24 p-6 transition-all ${collapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="max-w-6xl mx-auto space-y-6">
                <div><h1 className="text-2xl font-semibold">Recover QR PINs</h1>
                    <p className="mt-2 text-gray-600">Restore the original PINs from your Excel export to match your printed QR labels.</p></div>
                <div className="bg-white rounded-lg border p-6 space-y-4">
                    <p>Upload an .xlsx file with <strong>URL</strong> and <strong>PIN</strong> headers in the first row. The QR number is read from the end of each URL. Repeated SR / URL / PIN column groups and multiple sheets are supported.</p>
                    <p className="text-sm text-gray-600">Maximum 5 MB and 5,000 URL/PIN pairs. PINs must have four digits. Store PINs starting with zero as text. All rows must be valid and belong to QRs you can edit.</p>
                    <label className="block font-medium" htmlFor="recovery-workbook">Original Excel export</label>
                    <input id="recovery-workbook" type="file" accept=".xlsx" disabled={busy} onChange={chooseFile} className="block w-full" />
                    <Button disabled={!file || busy} onClick={() => submit(false)}>{busy ? 'Processing…' : 'Preview matches'}</Button>
                </div>
                {error && <div role="alert" className="border border-red-300 bg-red-50 text-red-800 p-4 rounded">{error}</div>}
                {result && <div role="status" className="border border-green-300 bg-green-50 text-green-800 p-4 rounded">Restored {result.updated} PINs. {result.unchanged} already matched. Existing scan history has been preserved.</div>}
                {preview && <section className="bg-white rounded-lg border p-6 space-y-4" aria-label="Recovery preview">
                    <h2 className="text-lg font-semibold">Review matches</h2>
                    <p>{preview.matched} unique QRs matched · {preview.changes} PINs to restore · {preview.invalid} invalid rows</p>
                    {preview.invalid > 0 && <p className="text-red-700">Fix the invalid rows in your workbook, then upload it again. No PINs have been changed.</p>}
                    {!preview.invalid && !preview.changes && <p>All matching PINs are already correct. No changes are needed.</p>}
                    <div className="overflow-x-auto"><table className="w-full text-sm text-left">
                        <thead><tr>{['Sheet / row / column', 'QR number', 'File PIN', 'Result'].map(label => <th key={label} className="p-3 border-b">{label}</th>)}</tr></thead>
                        <tbody>{rows.slice((page - 1) * 100, page * 100).map((row, i) => <tr key={i}>
                            <td className="p-3 border-b">{row.sheet} / {row.row} / {row.column}</td>
                            <td className="p-3 border-b font-mono">{row.uniqueNo || '—'}</td>
                            <td className="p-3 border-b font-mono">{row.pin || '—'}</td>
                            <td className={`p-3 border-b ${row.error ? 'text-red-700' : ''}`}>{row.error || row.status}</td>
                        </tr>)}</tbody>
                    </table></div>
                    {pageCount > 1 && <div className="flex gap-4 items-center"><Button disabled={page <= 1 || busy} onClick={() => setPage(page - 1)}>Previous</Button><span>Page {page} of {pageCount}</span><Button disabled={page >= pageCount || busy} onClick={() => setPage(page + 1)}>Next</Button></div>}
                    {!!preview.token && <><p>Restoring will replace the current PINs for these matching QRs with the PINs shown above. Review them against your original printed labels before continuing. This preview expires after 15 minutes.</p>
                        <Button disabled={busy} onClick={() => submit(true)}>{busy ? 'Restoring…' : `Restore ${preview.changes} PINs`}</Button></>}
                </section>}
            </div>
        </main>
    </div>;
}
